import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  forwardRef,
  useMergeRefs,
} from "@chakra-ui/react";

export interface TextFieldProps extends InputProps {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showClear?: boolean;
  onClear?: (event: React.MouseEvent) => void;
  clearColor?: string;
  label: string;
  errorText?: string;
  isInvalid?: boolean;
  isRequired?: boolean;
  simple?: boolean;
  dividerColor?: string;
  noRightPadding?: boolean;
}

export const TextField = forwardRef<TextFieldProps, "input">(
  (
    {
      leftIcon = null,
      rightIcon = null,
      onClear,
      showClear = false,
      clearColor,
      size,
      label,
      errorText = "",
      isInvalid = false,
      isRequired = false,
      value,
      onFocus,
      onBlur,
      simple = false,
      dividerColor,
      noRightPadding,
      ...rest
    },
    ref
  ) => {
    const [isLabelActive, setLabelActive] = useState(false);
    const [labelOffset, setLabelOffset] = useState<string | undefined>("");
    const inputRef = useRef<HTMLInputElement>(null);
    const [rightElementOffset, setRightElementOffset] = useState<
      number | undefined
    >(0);
    const rightElementRef = useRef<HTMLDivElement>(null);
    const leftElementRef = useRef<HTMLDivElement>(null);

    function touchHandler(e: any): void {
      // Don't allow scroll well typing input
      if (inputRef && inputRef.current) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        inputRef.current.blur();
      }
      e.preventDefault();
    }

    const onInputFocus = useCallback(
      (e) => {
        document.addEventListener("touchmove", touchHandler, false);
        if (onFocus) {
          onFocus(e);
        }
      },
      [onFocus]
    );

    const onInputBlur = useCallback(
      (e) => {
        document.removeEventListener("touchmove", touchHandler);

        if (inputRef && inputRef.current && inputRef?.current?.value.length) {
          setLabelActive(true);
        } else {
          setLabelActive(false);
        }

        if (onBlur) {
          onBlur(e);
        }
      },
      [onBlur, inputRef]
    );

    useEffect(() => {
      setRightElementOffset(rightElementRef?.current?.offsetWidth);
    }, [rightElementRef]);

    // We need to offset the label if there is a left element. We get the width of the element
    // and subtract 15 pixels (the width of the space between the left edge of the input box and the
    // left edge of the entire component). This lines upthe label with the actual text input.
    useEffect(() => {
      setLabelOffset(`${(leftElementRef?.current?.offsetWidth || 0) - 15}px`);
    }, [leftElementRef]);

    const clearButton = (
      <Button
        onClick={onClear}
        variant="ghost"
        sx={{
          bg: "transparent",
          mr: "8px",
          color: clearColor,
        }}
      >
        Clear
      </Button>
    );

    const refs = useMergeRefs(ref, inputRef);

    const labelDefaultStyle = {
      zIndex: 2,
      color: "medium_gray",
      position: "absolute",
      left: 0,
      ...(leftIcon && labelOffset && { pl: labelOffset }),
      top: "50%",
      transform: "translate(16px, -50%) scale(1)",
      m: "0px",
      bg: "transparent",
      visibility: simple && "hidden",
    };

    const labelActiveStyle = {
      ...labelDefaultStyle,
      transformOrigin: "top left",
      top: 0,
      transform: "translate(14px, -10px) scale(0.75)",
      bg: "white",
      pl: "xxsmall",
      pr: "xxsmall",
    };

    return (
      <FormControl isInvalid={isInvalid} isRequired={isRequired}>
        <FormLabel
          sx={
            (value && value.toString().length) || isLabelActive
              ? labelActiveStyle
              : labelDefaultStyle
          }
          _invalid={{ color: "error" }}
          _focus={{ ...labelActiveStyle, color: "primary" }}
        >
          {label}
        </FormLabel>
        <InputGroup size={size}>
          {leftIcon ? (
            <InputLeftElement ref={leftElementRef}>{leftIcon}</InputLeftElement>
          ) : null}
          <Input
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            pr={
              showClear && rightIcon
                ? `${(rightElementOffset || 0) + 80}px`
                : showClear || rightIcon
                ? undefined
                : 4
            }
            value={value}
            {...rest}
            ref={refs}
          />
          {showClear || rightIcon ? (
            <InputRightElement
              visibility={simple ? "hidden" : "visible"}
              _groupHover={{
                visibility: simple && "visible",
              }}
              width="auto"
              height="100%"
              pr={noRightPadding ? "0px" : simple ? "4px" : "16px"}
              ref={rightElementRef}
            >
              <Flex justifyContent="flex-end" alignItems="center" flex={1}>
                {showClear && clearButton}
                {showClear && (
                  <Box
                    sx={{
                      width: "1px",
                      height: "16px",
                      mr: rightIcon ? "8px" : "0px",
                      borderRight: rightIcon ? "1px solid" : "none",
                      borderRightColor: dividerColor || "medium_gray",
                      opacity: ".5",
                    }}
                  />
                )}
                {rightIcon}
              </Flex>
            </InputRightElement>
          ) : null}
        </InputGroup>
        <FormErrorMessage
          sx={{
            position: "absolute",
            fontSize: "xsm",
            pl: "16px",
            bottom: "-20px",
          }}
        >
          {errorText}
        </FormErrorMessage>
      </FormControl>
    );
  }
);
